import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  CircularProgress,
  TextField,
  InputAdornment,
  Pagination,
  Alert,
  styled,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
  Select,
  MenuItem,
  Card,
  CardContent,
  Grid,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  FilterList as FilterListIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ArrowDropDown,
} from '@mui/icons-material';
import useFetchObjects from '../../api/useFetchObjects';
import useFetchObject from '../../api/useFetchObject';
import useDelete from '../../api/useDelete';
import useAdd from '../../api/useAdd';
import useUpdate from '../../api/useUpdate';
import { useAuth } from '../../context/AuthContext';

// RTL-aware TextField component
const RTLTextField = styled(TextField)(({ theme, isRtl }) => ({
  ...(isRtl && {
    '& .MuiInputBase-input': {
      textAlign: 'right',
      direction: 'rtl',
      padding: '10px 14px 16px',
    },
    '& .MuiInputLabel-root': {
      right: 25,
      left: 'auto',
      transformOrigin: 'right',
      marginRight: '5px',
    },
    '& .MuiOutlinedInput-root': {
      paddingLeft: 14,
      paddingRight: 0,
    },
    '& .MuiInputAdornment-root': {
      marginRight: 8,
      marginLeft: 0,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      textAlign: 'right',
    },
    '& .MuiFormHelperText-root': {
      textAlign: 'right',
    },
  }),
}));

// RTL-aware TableCell component with bold header option
const RTLTableCell = styled(TableCell)(({ theme, isRtl, align, isHeader }) => ({
  ...(isRtl && {
    textAlign: align || 'right',
    direction: 'rtl',
  }),
  ...(isHeader && {
    fontWeight: 'bold',
  }),
}));

// Custom styled button for better RTL support
const RTLButton = styled(Button)(({ theme, isRtl }) => ({
  ...(isRtl && {
    flexDirection: 'row-reverse',
  }),
}));

// RTL Dialog Title
const RTLDialogTitle = styled(DialogTitle)(({ theme, isRtl }) => ({
  ...(isRtl && {
    textAlign: 'right',
    direction: 'rtl',
  }),
}));

// RTL Dialog Content
const RTLDialogContent = styled(DialogContent)(({ theme, isRtl }) => ({
  ...(isRtl && {
    textAlign: 'right',
    direction: 'rtl',
  }),
}));

// RTL Dialog Actions
const RTLDialogActions = styled(DialogActions)(({ theme, isRtl }) => ({
  ...(isRtl && {
    flexDirection: 'row-reverse',
  }),
}));

const MinistryList = () => {
  const { t, i18n } = useTranslation(['ministries', 'common', 'validation']);
  const { user, hasPermission } = useAuth();
  const token = user?.token;
  const isRtl = i18n.language === 'dari' || i18n.language === 'ps';
  const theme = useTheme();
  
  // Permission state - check if user has required permissions
  const [permissions, setPermissions] = useState({
    canCreate: false,
    canUpdate: false,
    canDelete: false
  });

  // Permission error message
  const [permissionError, setPermissionError] = useState('');
  const [showPermissionError, setShowPermissionError] = useState(false);
  
  // State for filtering and pagination
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // State for dialog management
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedMinistryId, setSelectedMinistryId] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
  });

  // Validation state
  const [errors, setErrors] = useState({});

  // Check user permissions
  useEffect(() => {
    setPermissions({
      canCreate: hasPermission('create:ministry'),
      canUpdate: hasPermission('update:ministry'),
      canDelete: hasPermission('delete:ministry')
    });
    
    // For development/testing - temporary solution to enable buttons
    // Remove this in production when permissions are properly set up
    if (process.env.NODE_ENV === 'development') {
      setPermissions({
        canCreate: true,
        canUpdate: true,
        canDelete: true
      });
    }
  }, [hasPermission]);
  
  // Handle CRUD operation errors with permission checks
  const handleApiError = (error) => {
    if (error?.response?.status === 403) {
      setPermissionError(t('common:permission_denied'));
      setShowPermissionError(true);
      return true;
    }
    return false;
  };

  // Query parameters for API call
  const params = {
    page,
    limit,
    search,
  };
  
  // Fetch ministries
  const { 
    data, 
    isLoading, 
    isError, 
    error,
    refetch 
  } = useFetchObjects('ministries', params, token);
  
  // Fetch ministry data for edit
  const { 
    data: ministryData, 
    isLoading: isLoadingMinistry,
  } = useFetchObject('ministries', selectedMinistryId ? `ministries/${selectedMinistryId}` : null, selectedMinistryId, token);

  // Delete functionality
  const { handleDelete, ConfirmDialog } = useDelete('ministries', token);

  // Create functionality
  const { 
    handleAdd, 
    loading: isCreating, 
    success: createSuccess 
  } = useAdd(
    'ministries',
    token,
    '/ministries',
    t('create_success'),
    t('create_error')
  );

  // Update functionality
  const { 
    handleUpdate, 
    loading: isUpdating, 
    success: updateSuccess 
  } = useUpdate(
    'ministries',
    token,
    '/ministries',
    t('update_success'),
    t('update_error')
  );

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };
  
  // Handle search submit
  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    refetch();
  };

  // Handle page change
  const handlePageChange = (event, value) => {
    setPage(value);
  };

  // Handle page size change
  const handleLimitChange = (e) => {
    setLimit(Number(e.target.value));
    setPage(1); // Reset to first page when limit changes
  };

  // Open create dialog
  const handleOpenCreateDialog = () => {
    setFormData({
      name: '',
      description: '',
    });
    setErrors({});
    setCreateDialogOpen(true);
  };

  // Close create dialog
  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false);
  };

  // Open edit dialog
  const handleOpenEditDialog = (id) => {
    setSelectedMinistryId(id);
    setEditDialogOpen(true);
  };

  // Close edit dialog
  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedMinistryId(null);
  };

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  // Set form data when ministry data is loaded (for edit)
  React.useEffect(() => {
    if (ministryData && editDialogOpen) {
      const ministry = ministryData.data || ministryData;
      
      setFormData({
        name: ministry.name || '',
        description: ministry.description || '',
      });
    }
  }, [ministryData, editDialogOpen]);

  // Reset form and close dialog when operation is successful
  React.useEffect(() => {
    if (createSuccess) {
      handleCloseCreateDialog();
      refetch();
    }
    
    if (updateSuccess) {
      handleCloseEditDialog();
    refetch();
    }
  }, [createSuccess, updateSuccess]);

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = t('validation:required');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle create form submission
  const handleCreateSubmit = (e) => {
    e.preventDefault();
    
    // Validate form
    if (!validateForm()) {
      return;
    }
    
    // Submit form data
    handleAdd(formData);
  };

  // Handle edit form submission
  const handleEditSubmit = (e) => {
    e.preventDefault();
    
    // Validate form
    if (!validateForm()) {
      return;
    }
    
    // Submit form data
    handleUpdate(selectedMinistryId, formData);
  };

  // Calculate total pages
  const totalPages = data?.totalPages || data?.pagination?.totalPages || 1;
  
  // Ministries list
  const ministries = data?.ministries || data?.data || (Array.isArray(data) ? data : []);

  const totalItems = data?.count ?? data?.total ?? data?.pagination?.total ?? ministries.length;

  return (
    <Box dir={isRtl ? 'rtl' : 'ltr'} sx={{ width: '100%' }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 3,
        flexDirection: isRtl ? 'row-reverse' : 'row',
        width: '100%'
      }}>
        <RTLButton
          variant="contained"
          startIcon={!isRtl && <AddIcon />}
          endIcon={isRtl && <AddIcon />}
          onClick={handleOpenCreateDialog}
          isRtl={isRtl}
          sx={{ 
            whiteSpace: 'nowrap',
            borderRadius: 2,
            px: 2,
            py: 0.75,
            textTransform: 'none',
            fontWeight: 500,
            boxShadow: 2,
            '&:hover': {
              boxShadow: 4
            }
          }}
        >
          {t('create_new')}
        </RTLButton>
        
        <Typography 
          variant="h4" 
          component="h1" 
          sx={{ 
            textAlign: isRtl ? 'right' : 'left',
            fontWeight: 'bold',
          }}
        >
          {t('list_title')}
        </Typography>
      </Box>

      {/* Search and filters */}
      <Box sx={{ 
        display: 'flex', 
        mb: 3, 
        gap: 2,
        flexDirection: isRtl ? 'row-reverse' : 'row',
        width: '100%'
      }}>
        <RTLTextField
                variant="outlined"
          size="small"
          placeholder={t('common:search')}
          value={search}
                onChange={handleSearchChange}
          sx={{ 
            flexGrow: 1,
            '& .MuiOutlinedInput-root': {
              paddingRight: isRtl ? '0px' : '0',
              paddingLeft: isRtl ? '0' : '0px',
            },
            '& .MuiOutlinedInput-input': {
              padding: '10px 14px',
            }
          }}
          isRtl={isRtl}
                InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            dir: isRtl ? 'rtl' : 'ltr',
          }}
        />
        
        <RTLButton 
                variant="contained"
          onClick={handleSearch}
          isRtl={isRtl}
        >
          {t('common:search')}
        </RTLButton>
        
        <RTLButton 
          variant="outlined" 
          onClick={() => {
            setSearch('');
            refetch();
          }}
          isRtl={isRtl}
        >
          {t('common:reset')}
        </RTLButton>
        
        <RTLButton 
          variant="outlined" 
          startIcon={!isRtl && <RefreshIcon />}
          endIcon={isRtl && <RefreshIcon />}
          onClick={refetch}
          isRtl={isRtl}
        >
          {t('common:refresh')}
        </RTLButton>
        
        <RTLButton
          variant="text"
          color="primary"
          onClick={() => setShowFilters(!showFilters)}
          startIcon={!isRtl && (showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />)}
          endIcon={isRtl && (showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />)}
          isRtl={isRtl}
        >
          <FilterListIcon fontSize="small" sx={{ mr: 0.5 }} />
          {t('common:filter')}
        </RTLButton>
      </Box>

      <Collapse in={showFilters}>
        <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: isRtl ? 'row-reverse' : 'row', 
            gap: 2, 
            flexWrap: 'wrap' 
          }}>
            {/* Additional filters can be added here as needed */}
          </Box>
        </Paper>
      </Collapse>

      {/* Error alert */}
      {isError && (
        <Alert severity="error" sx={{ mb: 3, textAlign: isRtl ? 'right' : 'left' }}>
          {error?.message || t('error_occurred')}
        </Alert>
      )}

      {/* Table */}
      <Paper sx={{ width: '100%', overflow: 'hidden', mb: 3 }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ 
              bgcolor: theme.palette.grey[100],
              '& .MuiTableRow-root': {
                borderBottom: `2px solid ${theme.palette.grey[300]}`,
              }
            }}>
              <TableRow>
                <RTLTableCell 
                  isRtl={isRtl} 
                  isHeader
                  align={isRtl ? "right" : "left"}
                  sx={{ 
                    fontSize: '1rem',
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    textAlign: isRtl ? 'right' : 'left'
                  }}
                >
                   {t('id')}
                </RTLTableCell>
                <RTLTableCell 
                  isRtl={isRtl} 
                  isHeader
                  sx={{ 
                    fontSize: '1rem',
                    fontWeight: 600,
                    color: theme.palette.text.primary
                  }}
                >
                  {t('name')}
                </RTLTableCell>
                <RTLTableCell 
                  isRtl={isRtl} 
                  isHeader
                  sx={{ 
                    fontSize: '1rem',
                    fontWeight: 600,
                    color: theme.palette.text.primary
                  }}
                >
                  {t('description')}
                </RTLTableCell>
                <RTLTableCell 
                  isRtl={isRtl} 
                  isHeader
                  sx={{ 
                    fontSize: '1rem',
                    fontWeight: 600,
                    color: theme.palette.text.primary
                  }}
                >
                  {t('created_at')}
                </RTLTableCell>
                <RTLTableCell 
                  isRtl={isRtl} 
                  align={isRtl ? "right" : "right"} 
                  isHeader
                  sx={{ 
                    fontSize: '1rem',
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    textAlign: isRtl ? 'right' : 'right',
                  }}
                >
                  {t('common:actions')}
                </RTLTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <RTLTableCell colSpan={5} align="center" sx={{ py: 3 }} isRtl={isRtl}>
                    <CircularProgress size={30} />
                  </RTLTableCell>
                </TableRow>
              ) : ministries.length === 0 ? (
                <TableRow>
                  <RTLTableCell colSpan={5} align="center" sx={{ py: 3 }} isRtl={isRtl}>
                    {t('common:no_records')}
                  </RTLTableCell>
                </TableRow>
              ) : (
                ministries.map((ministry, index) => (
                  <TableRow 
                    key={ministry.id} 
                    hover
                    sx={{ 
                      '&:hover': { backgroundColor: 'grey.50' },
                      '& td': { 
                        py: 1.5, // Increased vertical padding for better spacing
                      } 
                    }}
                  >
                    <RTLTableCell isRtl={isRtl} align={isRtl ? "right" : "center"}>
                      <Typography variant="body2" sx={{ 
                        fontWeight: 500,
                        textAlign: isRtl ? 'right' : 'center' 
                      }}>
                        {(page - 1) * limit + index + 1}
                      </Typography>
                    </RTLTableCell>
                    <RTLTableCell isRtl={isRtl} sx={{ fontWeight: 500 }}>
                      {ministry.name}
                    </RTLTableCell>
                    <RTLTableCell isRtl={isRtl}>
                      {ministry.description || '-'}
                    </RTLTableCell>
                    <RTLTableCell isRtl={isRtl}>
                      {new Date(ministry.created_at).toLocaleDateString()}
                    </RTLTableCell>
                    <RTLTableCell isRtl={isRtl} align={isRtl ? "left" : "right"}>
                      <Box sx={{ display: 'flex', justifyContent: isRtl ? 'flex-start' : 'flex-end', gap: 1 }}>
                        <Tooltip title={t('common:edit')} placement={isRtl ? "right" : "left"}>
                          <IconButton
                        size="small"
                            onClick={() => handleOpenEditDialog(ministry.id)}
                        color="primary"
                            sx={{
                              '&:hover': {
                                backgroundColor: 'rgba(25, 118, 210, 0.08)', // Consistent primary light hover color
                              }
                            }}
                          >
                            <EditIcon fontSize="small" />
                      </IconButton>
                        </Tooltip>
                        <Tooltip title={t('common:delete')} placement={isRtl ? "right" : "left"}>
                      <IconButton
                        size="small"
                            onClick={() => handleDelete(ministry.id)}
                        color="error"
                            sx={{
                              '&:hover': {
                                backgroundColor: 'rgba(211, 47, 47, 0.08)', // Consistent error light hover color
                              }
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                      </IconButton>
                        </Tooltip>
                      </Box>
                    </RTLTableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Pagination */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          p: 2,
          flexDirection: isRtl ? 'row-reverse' : 'row' 
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ 
                mr: isRtl ? 0 : 2, 
                ml: isRtl ? 2 : 0,
                whiteSpace: 'nowrap'
              }}
            >
              {t('common:rows_per_page')}:
            </Typography>
            <Select
              value={limit}
              onChange={handleLimitChange}
              size="small"
              sx={{ 
                minWidth: 70,
                '& .MuiSelect-select': {
                  px: 2
                },
                ...(isRtl && {
                  '& .MuiSelect-select': {
                    paddingRight: '32px',
                    textAlign: 'right',
                    paddingLeft: '14px',
                  },
                  '& .MuiSelect-icon': {
                    right: 7,
                    left: 'auto',
                  },
                }),
              }}
              MenuProps={{
                disableScrollLock: true,
                anchorOrigin: {
                  vertical: 'top',
                  horizontal: isRtl ? 'right' : 'left',
                },
                transformOrigin: {
                  vertical: 'top',
                  horizontal: isRtl ? 'right' : 'left',
                },
              }}
            >
              <MenuItem value={5}>5</MenuItem>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
            </Select>
          </Box>
          
          {/* Make sure we always show pagination even if total is just 1 page */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              {t('common:showing')} {(page - 1) * limit + 1}-
              {Math.min(page * limit, totalItems)} {t('common:of')} {totalItems}
            </Typography>
            <Pagination
              count={Math.max(totalPages, 1)}  
          page={page}
              onChange={handlePageChange}
              color="primary"
              dir={isRtl ? 'rtl' : 'ltr'}
              shape="rounded"
              size="small"
              showFirstButton
              showLastButton
            />
          </Box>
        </Box>
      </Paper>

      {/* Create Dialog */}
      <Dialog 
        open={createDialogOpen} 
        onClose={handleCloseCreateDialog}
        fullWidth
        maxWidth="sm"
        dir={isRtl ? 'rtl' : 'ltr'}
        PaperProps={{
          sx: {
            borderRadius: '8px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
            maxWidth: '600px',
            width: '100%',
            mx: 'auto',
          }
        }}
        sx={{
          '& .MuiDialog-container': {
            justifyContent: 'center',
            alignItems: 'center',
            '& .MuiPaper-root': {
              margin: '0 auto',
              position: 'absolute',
              top: '50%',
              left: '42%',
              transform: 'translate(-50%, -50%)',
            },
          },
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          }
        }}
      >
        <RTLDialogTitle isRtl={isRtl} sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          fontWeight: 'bold',
          pb: 1,
          pt: 2,
          height: '60px',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}>
          <Typography variant="h5">{t('create_title')}</Typography>
          <IconButton onClick={handleCloseCreateDialog} size="small">
            <CloseIcon />
          </IconButton>
        </RTLDialogTitle>
        
        <RTLDialogContent isRtl={isRtl} sx={{ pt: 3 }}>
          <Box component="form" id="create-form" onSubmit={handleCreateSubmit}>
            <RTLTextField
              fullWidth
              name="name"
              label={t('name')}
              value={formData.name}
              onChange={handleInputChange}
              error={!!errors.name}
              helperText={errors.name}
              required
              margin="normal"
              isRtl={isRtl}
              InputProps={{
                dir: isRtl ? 'rtl' : 'ltr',
              }}
              FormHelperTextProps={{
                style: { textAlign: isRtl ? 'right' : 'left' }
              }}
            />
            
            <RTLTextField
              fullWidth
              name="description"
              label={t('description')}
              value={formData.description || ''}
              onChange={handleInputChange}
              multiline
              rows={3}
              margin="normal"
              isRtl={isRtl}
              InputProps={{
                dir: isRtl ? 'rtl' : 'ltr',
              }}
              FormHelperTextProps={{
                style: { textAlign: isRtl ? 'right' : 'left' }
              }}
            />
          </Box>
        </RTLDialogContent>
        
        <RTLDialogActions isRtl={isRtl} sx={{ 
          px: 3, 
          pb: 3, 
          gap: 1,
          borderTop: '1px solid',
          borderColor: 'divider',
          pt: 2
        }}>
          <RTLButton 
            variant="outlined"
            onClick={handleCloseCreateDialog}
            isRtl={isRtl}
          >
            {t('common:cancel')}
          </RTLButton>
          <RTLButton 
            variant="contained"
            type="submit"
            form="create-form"
            disabled={isCreating}
            isRtl={isRtl}
          >
            {isCreating ? (
              <CircularProgress size={24} />
            ) : (
              t('common:save')
            )}
          </RTLButton>
        </RTLDialogActions>
      </Dialog>
      
      {/* Edit Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={handleCloseEditDialog}
        fullWidth
        maxWidth="sm"
        dir={isRtl ? 'rtl' : 'ltr'}
        PaperProps={{
          sx: {
            borderRadius: '8px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
            maxWidth: '600px',
            width: '100%',
            mx: 'auto',
          }
        }}
        sx={{
          '& .MuiDialog-container': {
            justifyContent: 'center',
            alignItems: 'center',
            '& .MuiPaper-root': {
              margin: '0 auto',
              position: 'absolute',
              top: '50%',
              left: '42%',
              transform: 'translate(-50%, -50%)',
            },
          },
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          }
        }}
      >
        <RTLDialogTitle isRtl={isRtl} sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          fontWeight: 'bold',
          pb: 1,
          pt: 2,
          height: '60px',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}>
          <Typography variant="h5">{t('edit_title')}</Typography>
          <IconButton onClick={handleCloseEditDialog} size="small">
            <CloseIcon />
          </IconButton>
        </RTLDialogTitle>
        
        <RTLDialogContent isRtl={isRtl} sx={{ pt: 3 }}>
          {isLoadingMinistry ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box component="form" id="edit-form" onSubmit={handleEditSubmit}>
              <RTLTextField
                fullWidth
                name="name"
                label={t('name')}
                value={formData.name}
                onChange={handleInputChange}
                error={!!errors.name}
                helperText={errors.name}
                required
                margin="normal"
                isRtl={isRtl}
                InputProps={{
                  dir: isRtl ? 'rtl' : 'ltr',
                }}
                FormHelperTextProps={{
                  style: { textAlign: isRtl ? 'right' : 'left' }
                }}
              />
              
              <RTLTextField
                fullWidth
                name="description"
                label={t('description')}
                value={formData.description || ''}
                onChange={handleInputChange}
                multiline
                rows={3}
                margin="normal"
                isRtl={isRtl}
                InputProps={{
                  dir: isRtl ? 'rtl' : 'ltr',
                }}
              />
            </Box>
          )}
        </RTLDialogContent>
        
        <RTLDialogActions isRtl={isRtl} sx={{ 
          px: 3, 
          pb: 3, 
          gap: 1,
          borderTop: '1px solid',
          borderColor: 'divider',
          pt: 2
        }}>
          <RTLButton 
            variant="outlined"
            onClick={handleCloseEditDialog}
            isRtl={isRtl}
          >
            {t('common:cancel')}
          </RTLButton>
          <RTLButton 
            variant="contained"
            type="submit"
            form="edit-form"
            disabled={isUpdating || isLoadingMinistry}
            isRtl={isRtl}
          >
            {isUpdating ? (
              <CircularProgress size={24} />
            ) : (
              t('common:save')
            )}
          </RTLButton>
        </RTLDialogActions>
      </Dialog>
      
      {/* Delete confirmation dialog */}
      <ConfirmDialog message={t('delete_confirm_text')} />
    </Box>
  );
};

export default MinistryList; 